import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { wishlistKeys } from './queryKeys'
import { wishlistApi } from '../../lib'

import type { CustomerWishlistResponse } from '../../lib/apiClient'
import type { WishlistItem } from '../../types'

interface UseWishlistQueryResult {
  wishlistId: string | null
  wishlistItems: WishlistItem[]
  itemsCount: number
  isLoggedIn: boolean
}

export function useWishlistQuery(): UseWishlistQueryResult {
  const { data: wishlistData, isSuccess } = useQuery<CustomerWishlistResponse>({
    queryKey: wishlistKeys.customer(),
    queryFn: () => wishlistApi.getCustomerWishlist(),
    retry: false, // Override default (1) - don't retry auth-dependent query
    staleTime: 5 * 60 * 1000, // 5 minutes - wishlist does not change externally
    gcTime: 10 * 60 * 1000, // 10 minutes cache lifetime
  })

  // Derive state directly from query data (no useEffect + useState)
  const { wishlistId, wishlistItems, itemsCount, isLoggedIn } = useMemo(() => {
    const wishlist = wishlistData?.customer?.wishlist

    return {
      wishlistId: wishlist?.id ?? null,
      wishlistItems: wishlist?.items_v2?.items ?? [],
      itemsCount: wishlist?.items_count ?? 0,
      isLoggedIn: isSuccess && !!wishlist,
    }
  }, [wishlistData, isSuccess])

  return {
    wishlistId,
    wishlistItems,
    itemsCount,
    isLoggedIn,
  }
}
